import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import MailCard from './MailCard';
import api from '../../api/axios'; 
import { RotateCcw, Search, Copy, Check, Filter, ShieldOff } from 'lucide-react';

const BoxRow = forwardRef(({ email, globalFilter, globalFolder, metadata, onGlobalFilterChange }, ref) => { 
  const [mails, setMails] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const [filterText, setFilterText] = useState("");
  const [filterFolder, setFilterFolder] = useState("ALL");
  const [showSearch, setShowSearch] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const MAX_EMAILS = 40;

  const fetchInitial = useCallback(async () => {
    try {
      const res = await api.get(`/metadata/by-account?email=${email}`); 
      if (res.data && res.data.content) {
        setMails(res.data.content);
      }
    } catch (e) { 
      console.log("Error fetching DB mails for:", email); 
    }
  }, [email]);
  
  useEffect(() => {
    if (globalFilter !== undefined) {
      setFilterText(globalFilter);
      setShowSearch(!!globalFilter);
    }
  }, [globalFilter]);

  useEffect(() => {
    if (globalFolder !== undefined) setFilterFolder(globalFolder);
  }, [globalFolder]);

  const filteredMails = mails.filter(mail => {
    const term = filterText.toLowerCase();
    const matchesText = !term || (
      (mail.subject?.toLowerCase().includes(term)) ||
      (mail.sender?.toLowerCase().includes(term)) ||
      (mail.originIp?.includes(term))
    );
    
    const folder = (mail.folderName || "INBOX").toLowerCase();
    const isSpam = folder.includes("spam") || folder.includes("junk");
    
    const matchesFolder = filterFolder === "ALL" || 
      (filterFolder === "SPAM" && isSpam) ||
      (filterFolder === "INBOX" && !isSpam);
    
    return matchesText && matchesFolder;
  });

const handleRefresh = useCallback(async () => {
    if (!metadata?.active || isRefreshing) return; 

    setIsRefreshing(true); 

    try {
        await api.post('/mail/start-listener', { email }); 
        await fetchInitial(); 

        setTimeout(() => {
            setIsRefreshing(false); 
        }, 6000); 

    } catch (err) {
        console.error("Failed to start listener", err);
        setIsRefreshing(false);
    }
}, [email, metadata?.active, isRefreshing, fetchInitial]);

  useImperativeHandle(ref, () => ({
    triggerRefresh: () => handleRefresh(),
    getVisibleIps: () => filteredMails.map(m => m.originIp).filter(ip => ip && ip !== 'N/A')
  }));

  useImperativeHandle(ref, () => ({
    triggerRefresh: () => handleRefresh(),
    getVisibleIps: () => filteredMails.map(m => m.originIp).filter(ip => ip && ip !== 'N/A')
  }));

useEffect(() => {
    if (metadata && metadata.active === false) {
      setMails([]);
      setIsConnected(false);
      return;
    }

    fetchInitial();

    const socket = new SockJS('https://api.ymoghar.engineer/ws');
    const stompClient = Stomp.over(() => socket);
    stompClient.debug = () => {}; 
    
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    stompClient.connect(headers, () => {
        setIsConnected(true);
        const topic = `/topic/emails/${email}`; 
        stompClient.subscribe(topic, (message) => {
            const newMail = JSON.parse(message.body);
            setMails(prev => {
                if (prev.find(m => m.messageId === newMail.messageId)) return prev;
                return [newMail, ...prev].slice(0, MAX_EMAILS);
            });
        });
    }, (err) => {
        setIsConnected(false);
    });

    return () => {
        if (stompClient && stompClient.connected) {
            stompClient.disconnect();
        }
    };
  }, [email, metadata, fetchInitial]); 

  const handleCopyIps = () => {
    const ips = [...new Set(filteredMails.map(m => m.originIp).filter(ip => ip && ip !== 'N/A'))];
    if (ips.length) {
      navigator.clipboard.writeText(ips.join('\n'));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  return (
    <div className={`mb-2 border rounded shadow-sm overflow-hidden ${!metadata?.active ? 'opacity-75 bg-light' : 'bg-white'}`}> 
      {/* Header  */}
      <div className="d-flex align-items-center justify-content-between px-3 py-1 bg-light border-bottom" style={{height: '36px'}}>
        <div className="d-flex align-items-center gap-2">
           {/* cnx status*/}
           <span 
             className={`rounded-circle ${isConnected && metadata?.active ? 'bg-success' : 'bg-secondary'}`} 
             style={{width: '8px', height: '8px', boxShadow: isConnected && metadata?.active ? '0 0 4px #28a745' : 'none'}}
           ></span>
           <span className={`fw-bold ${!metadata?.active ? 'text-muted' : 'text-dark'}`} style={{fontSize: '13px', maxWidth: '200px'}}>
             {email}
           </span>
           {/* status*/}
           {metadata?.active && <span className="badge bg-info p-1" style={{fontSize: '8px'}}>LISTENING</span>}
           {!metadata?.active && <span className="badge bg-danger bg-opacity-10 text-danger border-danger border p-1" style={{fontSize: '8px'}}>DISABLED</span>}
        </div>

        <div className="d-flex align-items-center gap-3">
            {metadata?.active && (
              <>
                <div className="d-flex align-items-center">
                    <Filter size={12} className="text-muted me-1"/>
                    <select 
                        className="form-select form-select-sm border-0 bg-transparent py-0 px-1 text-secondary fw-bold shadow-none" 
                        style={{fontSize: '11px', width: 'auto'}}
                        value={filterFolder}
                        onChange={(e) => setFilterFolder(e.target.value)}
                    >
                        <option value="ALL">ALL</option>
                        <option value="INBOX">INBOX</option>
                        <option value="SPAM">SPAM</option>
                    </select>
                </div>
                <div className="vr text-muted opacity-25" style={{height: '14px'}}></div>
              </>
            )}

            <div className="d-flex align-items-center gap-2">
                {metadata?.active && (
                  <>
                    <button 
                      className={`btn btn-sm ${isCopied ? 'btn-success' : 'btn-dark'} d-flex align-items-center gap-1 px-2`} 
                      style={{ fontSize: '10px', height: '24px' }}
                      onClick={handleCopyIps} 
                      title="Copy IPs"
                    >
                        {isCopied ? <Check size={12}/> : <Copy size={12} />}
                        <span>Copy IPs</span>
                    </button>
                    {/* searsh*/}
                    <div className="d-flex align-items-center" style={{ minWidth: showSearch ? '120px' : 'auto', transition: 'all 0.3s ease' }}>
                        {showSearch ? (
                            <input 
                                autoFocus
                                type="text" 
                                className="form-control border-0 bg-white shadow-sm px-2 py-0" 
                                style={{fontSize: '11px', height: '22px', borderRadius: '4px'}}
                                placeholder="Search..." 
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                onBlur={() => { if(!filterText) setShowSearch(false); }}
                            />
                        ) : (
                            <button className="btn btn-link p-0 text-muted" onClick={() => setShowSearch(true)}>
                                <Search size={14} />
                            </button>
                        )}
                    </div>
                    <button onClick={handleRefresh} disabled={isRefreshing} className="btn btn-link p-0 text-muted" title="Refresh Listener">
                        <RotateCcw size={14} className={isRefreshing ? "spin-animation" : ""} />
                    </button>
                  </>
                )}
                {/* mail nbr*/}
                <span className="badge bg-secondary bg-opacity-10 text-secondary border rounded-pill" style={{fontSize: '10px', minWidth: '30px'}}>
                    {metadata?.active ? filteredMails.length : 0}
                </span>
            </div>
        </div>
      </div>

      {/* Cards Area */}
      <div className="d-flex overflow-auto p-2 align-items-center" style={{ gap: '8px', minHeight: '110px' }}>
        {!metadata?.active ? (
            <div className="w-100 text-center text-muted small d-flex align-items-center justify-content-center gap-2">
               <ShieldOff size={14} /> Listener is currently disabled. Enable it from Account Manager.
            </div>
        ) : filteredMails.length === 0 ? (
            <div className="w-100 text-center text-muted small italic">
               {isConnected ? "Listening for live emails..." : "Connecting to stream..."}
            </div>
        ) : (
            filteredMails.map((mail, idx) => (
                <MailCard 
                    key={mail.messageId || idx} 
                    mail={mail} 
                    onFilterClick={(val) => onGlobalFilterChange && onGlobalFilterChange(val === globalFilter ? "" : val)} 
                />
            ))
        )}
      </div>

      <style>{`
        .spin-animation { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(-360deg); } }
      `}</style>
    </div>
  );
});

export default BoxRow;